import { useState } from 'react';
import { api, fmt, fmtDate, type AnalyticsItem } from '../api';
import { useAsync } from '../hooks';

const iso = (d: Date) => d.toISOString().slice(0, 10);
const LIMIT = 50;

const rubles = (kopecks: number) =>
  new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0,
  }).format(kopecks / 100);

function stateLabel(state: string | null): string {
  switch (state) {
    case 'PENDING': return 'На подтверждении';
    case 'HELD': return 'В холде';
    case 'AVAILABLE': return 'Доступно';
    case 'REJECTED': return 'Не начисляем';
    case 'CANCELLED': return 'Отменено';
    case 'CLAWED_BACK': return 'Возвращено';
    case 'QUARANTINED': return 'Требует разбора';
    default: return 'Ожидает подтверждения';
  }
}

function typeLabel(type: string, paidAt: string | null): string {
  switch (type) {
    case 'NEW_CLIENT': return 'Новый клиент';
    case 'NEW_CLIENT_IN_YEAR': return 'Новый клиент в учебном году';
    case 'UPSELL': return 'Доп. продажа';
    case 'RENEWAL': return 'Продление';
    case 'OTHER': return 'Другое';
    case 'UNKNOWN': return paidAt ? 'UNKNOWN' : 'Ожидает подтверждения';
    default: return type;
  }
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="stat">
      <div className="v">{typeof value === 'number' ? fmt(value) : value}</div>
      <div className="l">{label}</div>
    </div>
  );
}

export function AnalyticsPage() {
  const [from, setFrom] = useState(iso(new Date(Date.now() - 30 * 86_400_000)));
  const [to, setTo] = useState(iso(new Date()));
  const [state, setState] = useState('');
  const [orderType, setOrderType] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);

  const { data, loading, error } = useAsync(
    () =>
      api.analytics({
        from: new Date(`${from}T00:00:00`).toISOString(),
        to: new Date(`${to}T23:59:59`).toISOString(),
        state: state || undefined,
        orderType: orderType || undefined,
        query: query || undefined,
        limit: LIMIT,
        offset,
      }),
    [from, to, state, orderType, query, offset],
  );

  const s = data?.summary;
  const first = data && data.total > 0 ? offset + 1 : 0;
  const last = data ? Math.min(offset + data.items.length, data.total) : 0;

  return (
    <>
      <h1>Аналитика</h1>
      <p className="subtitle">
        Реферальные заказы, начисления и причины отказов. Период считается по дате создания заказа.
      </p>

      <div className="row" style={{ marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          type="date"
          style={{ width: 170 }}
          value={from}
          onChange={(e) => {
            setFrom(e.target.value);
            setOffset(0);
          }}
        />
        <input
          type="date"
          style={{ width: 170 }}
          value={to}
          onChange={(e) => {
            setTo(e.target.value);
            setOffset(0);
          }}
        />

        <select
          value={state}
          onChange={(e) => {
            setState(e.target.value);
            setOffset(0);
          }}
        >
          <option value="">Все статусы</option>
          <option value="HELD">В холде</option>
          <option value="AVAILABLE">Доступно</option>
          <option value="REJECTED">Не начисляем</option>
          <option value="PENDING">На подтверждении</option>
          <option value="CANCELLED">Отменено</option>
          <option value="CLAWED_BACK">Возвращено</option>
          <option value="QUARANTINED">Требует разбора</option>
        </select>

        <select
          value={orderType}
          onChange={(e) => {
            setOrderType(e.target.value);
            setOffset(0);
          }}
        >
          <option value="">Все типы</option>
          {(data?.byOrderType ?? []).map((r) => (
            <option key={r.orderType} value={r.orderType}>
              {typeLabel(r.orderType, null)}
            </option>
          ))}
        </select>

        <form
          className="row"
          onSubmit={(e) => {
            e.preventDefault();
            setOffset(0);
            setQuery(searchInput.trim());
          }}
        >
          <input
            style={{ width: 240 }}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Заказ, ученик, промокод…"
          />
          <button className="secondary small" type="submit">Найти</button>
        </form>
      </div>

      {error && <div className="notice err">{error}</div>}
      {loading && <p className="subtitle">Считаем…</p>}

      {s && (
        <>
          <div className="grid cols-4">
            <Stat label="Всего реферальных заказов" value={s.totalOrders} />
            <Stat label="Оплачено" value={s.paidOrders} />
            <Stat label="Подходят под начисление" value={s.eligibleOrders} />
            <Stat label="Сейчас в холде" value={s.heldOrders} />
          </div>

          <div className="grid cols-4" style={{ marginTop: 12 }}>
            <Stat label="Не начисляем" value={s.rejectedOrders} />
            <Stat label="Отменено / возвращено заказов" value={s.reversedOrders} />
            <Stat label="Бонусов в холде" value={s.heldBonuses} />
            <Stat label="Бонусов доступно" value={s.availableBonuses} />
          </div>

          {s.quarantinedOrders > 0 && (
            <div className="notice warn" style={{ marginTop: 16 }}>
              {fmt(s.quarantinedOrders)} заказов требуют ручного разбора.
            </div>
          )}

          <h2>По типам заказов</h2>
          <div className="card">
            <table>
              <thead>
                <tr>
                  <th>Тип</th>
                  <th className="num">Всего</th>
                  <th className="num">Оплачено</th>
                  <th className="num">Eligible</th>
                  <th className="num">Отклонено</th>
                  <th className="num">Активных бонусов</th>
                </tr>
              </thead>
              <tbody>
                {data!.byOrderType.map((r) => (
                  <tr key={r.orderType}>
                    <td>{typeLabel(r.orderType, null)}</td>
                    <td className="num">{fmt(r.orders)}</td>
                    <td className="num">{fmt(r.paid)}</td>
                    <td className="num">{fmt(r.eligible)}</td>
                    <td className="num">{fmt(r.rejected)}</td>
                    <td className="num">{fmt(r.activeBonuses)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2>Реферальные заказы</h2>
          <div className="card" style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Дата</th>
                  <th>Заказ</th>
                  <th>Реферер</th>
                  <th>Приглашённый</th>
                  <th className="num">Сумма</th>
                  <th>Товар</th>
                  <th>Тип</th>
                  <th className="num">Бонус</th>
                  <th>Статус</th>
                  <th>Причина</th>
                </tr>
              </thead>
              <tbody>
                {data!.items.map((r: AnalyticsItem) => (
                  <tr key={r.external_order_id}>
                    <td>{fmtDate(r.paid_at ?? r.placed_at)}</td>
                    <td>{r.external_order_id}</td>
                    <td>{r.referrer_external_id ?? '—'}</td>
                    <td>{r.referee_external_id}</td>
                    <td className="num">{r.amount_kopecks > 0 ? rubles(r.amount_kopecks) : '—'}</td>
                    <td>{r.product_external_id ?? '—'}</td>
                    <td>{typeLabel(r.order_type, r.paid_at)}</td>
                    <td className="num">{r.bonus_amount === null ? '—' : fmt(r.bonus_amount)}</td>
                    <td>{stateLabel(r.accrual_state)}</td>
                    <td>{r.reject_reason ?? '—'}</td>
                  </tr>
                ))}

                {data!.items.length === 0 && (
                  <tr>
                    <td colSpan={10} className="muted">По выбранным фильтрам заказов нет</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="row between" style={{ marginTop: 12 }}>
            <span className="muted">
              Показано {first}–{last} из {fmt(data!.total)}
            </span>

            <div className="row">
              <button
                className="secondary small"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - LIMIT))}
              >
                ← Назад
              </button>

              <button
                className="secondary small"
                disabled={offset + LIMIT >= data!.total}
                onClick={() => setOffset(offset + LIMIT)}
              >
                Дальше →
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
